import {Badge, Box, Stack, Table, Text} from '@mantine/core'

import type {GiftCommitmentsData} from 'src/api/client/models/analytics'
import {SectionCard} from 'src/components/section-card'
import {formatCurrency, formatNumber} from 'src/lib/format'

type Props = {
    data: GiftCommitmentsData
}

/** Title + campaign-goal blurb for gap analysis (SectionCard header or export). */
export function GiftCommitmentsHeader({data}: {data: GiftCommitmentsData}) {
    return (
        <Stack gap={4}>
            <Text fw={600} size="lg">
                Campaign Gap Analysis | Gift Table
            </Text>
            <Text size="sm" c="dimmed">
                Required prospects vs. identified prospects by gift level. Campaign goal{' '}
                <Text span fw={600}>
                    {formatCurrency(data.campaignGoal)}
                </Text>
                .
            </Text>
        </Stack>
    )
}

function isMet(numberRequired: number | null, numberIdentified: number): numberRequired is number {
    return numberRequired === null || numberIdentified >= numberRequired
}

export function GiftCommitmentsTable({
    data,
    maw = 600,
}: {
    data: GiftCommitmentsData
    /** Max width of the table wrapper; use `"100%"` in tight side-by-side layouts. */
    maw?: number | string
}) {
    return (
        <Box maw={maw} w="100%" miw={0}>
            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Gift Level</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Required</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Identified</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Status</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Gap</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.rows.map((row) => {
                        const met = isMet(row.numberRequired, row.numberIdentified)
                        const gapNumeric =
                            row.numberRequired != null
                                ? row.numberIdentified - row.numberRequired
                                : null

                        return (
                            <Table.Tr key={row.sortOrder}>
                                <Table.Td>
                                    {row.giftSize >= 10_000
                                        ? formatCurrency(row.giftSize)
                                        : 'Under $10,000'}
                                </Table.Td>
                                <Table.Td style={{textAlign: 'right'}}>
                                    {row.numberRequired != null
                                        ? formatNumber(row.numberRequired)
                                        : 'Many'}
                                </Table.Td>
                                <Table.Td style={{textAlign: 'right'}}>
                                    {formatNumber(row.numberIdentified)}
                                </Table.Td>
                                <Table.Td style={{textAlign: 'right'}}>
                                    {met ? (
                                        <Badge color="green" variant="light">
                                            MET
                                        </Badge>
                                    ) : (
                                        <Badge color="purple" variant="light">
                                            GAP
                                        </Badge>
                                    )}
                                </Table.Td>
                                <Table.Td style={{textAlign: 'right'}}>
                                    {gapNumeric === null
                                        ? '—'
                                        : met
                                          ? '✓'
                                          : formatNumber(gapNumeric)}
                                </Table.Td>
                            </Table.Tr>
                        )
                    })}
                </Table.Tbody>
            </Table>
        </Box>
    )
}

/** SectionCard title block + table, without SectionCard — used only for PNG export. */
export function GiftCommitmentsExportContent({data}: {data: GiftCommitmentsData}) {
    return (
        <Stack>
            <GiftCommitmentsHeader data={data} />
            <GiftCommitmentsTable data={data} />
        </Stack>
    )
}

export default function GiftCommitments({data}: Props) {
    return (
        <SectionCard title={<GiftCommitmentsHeader data={data} />}>
            <GiftCommitmentsTable data={data} />
        </SectionCard>
    )
}
